'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Sidebar } from './Sidebar';
import { Menu, ArrowLeft } from 'lucide-react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { CurrencySelector } from '@/components/ui/CurrencySelector';

interface PortalLayoutProps {
  children: React.ReactNode;
}

export const PortalLayout: React.FC<PortalLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { customer } = useCustomerAuth();
  const pathname = usePathname();

  const locale = pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] ?? 'en';

  // Get initials for avatar
  const getInitials = () => {
    if (!customer) return 'U';
    const first = customer.firstName?.[0] || '';
    const last = customer.lastName?.[0] || '';
    return (first + last).toUpperCase() || customer.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    // Use fixed positioning to break out of any parent container constraints
    <div className="fixed inset-0 bg-stone-50 flex z-50">
      {/* Sidebar Navigation */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden h-16 bg-white border-b border-stone-200 flex items-center px-4 justify-between flex-shrink-0">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 -ml-2 mr-2 text-stone-600 hover:bg-stone-100 rounded-md"
            >
              <Menu size={24} />
            </button>
            <Link
              href={`/${locale}/`}
              className="p-1.5 mr-1 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-md transition-colors"
              title="Back to Website"
            >
              <ArrowLeft size={18} />
            </Link>
            <span className="font-bold text-stone-900">ROYALE</span>
          </div>
          <div className="flex items-center gap-2">
            <CurrencySelector variant="minimal" className="text-stone-600" />
            <div className="h-8 w-8 bg-stone-200 rounded-full flex items-center justify-center text-xs font-bold text-stone-600">
              {getInitials()}
            </div>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-12">
          <div className="max-w-7xl mx-auto w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};
